/**
 * FormSection Component
 * Renders a section of a form with its fields
 * Supports repeatable sections, calculated fields, and conditional display/disable
 */

import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSection as FormSectionType, DisplayCondition } from '../../types/form';
import { FormField } from './FormField';
import { SourceDocUpload } from './SourceDocUpload';
import { Plus, Trash2 } from 'lucide-react';

interface FormSectionProps {
  section: FormSectionType;
  disabled?: boolean;
  subjectId?: string;
  dataEntryId?: string;
}

/**
 * Evaluate a display/disable condition against a watched form value.
 * Handles both scalar and array values (for checkboxes).
 */
const evaluateCondition = (condition: DisplayCondition, watchValue: any): boolean => {
  const condValue = condition.value;

  // Handle array values (e.g. checkbox fields)
  if (Array.isArray(watchValue)) {
    switch (condition.operator) {
      case '==':
      case 'eq':
        return watchValue.includes(condValue);
      case '!=':
      case 'ne':
        return !watchValue.includes(condValue);
      case 'contains':
        return watchValue.includes(condValue);
      case 'notEmpty':
        return watchValue.length > 0;
      default:
        return true;
    }
  }

  // Scalar values
  switch (condition.operator) {
    case '==':
    case 'eq':
      return watchValue === condValue;
    case '!=':
    case 'ne':
      return watchValue !== condValue;
    case '>':
      return Number(watchValue) > Number(condValue);
    case '>=':
      return Number(watchValue) >= Number(condValue);
    case '<':
      return Number(watchValue) < Number(condValue);
    case '<=':
      return Number(watchValue) <= Number(condValue);
    case 'contains':
      return typeof watchValue === 'string' && typeof condValue === 'string'
        ? watchValue.includes(condValue)
        : false;
    case 'notEmpty':
      return watchValue !== undefined && watchValue !== null && watchValue !== '';
    default:
      return true;
  }
};

export const FormSection: React.FC<FormSectionProps> = ({
  section,
  disabled = false,
  subjectId,
  dataEntryId,
}) => {
  const { watch, setValue } = useFormContext();
  const [repeatCount, setRepeatCount] = useState(1);

  // For repeatable sections
  const maxRepeats = section.maxRepeats || 10;

  // Handle calculated fields (e.g. BMI)
  useEffect(() => {
    const calculatedFields = section.fields.filter(
      (f) => f.type === 'calculated' && f.calculation
    );

    if (calculatedFields.length === 0) return;

    const subscription = watch((values) => {
      calculatedFields.forEach((field) => {
        if (!field.calculation) return;

        const { formula, dependsOn } = field.calculation;

        if (formula === 'BMI' && dependsOn.length === 2) {
          const weight = Number(values[dependsOn[0]]); // WEIGHT
          const height = Number(values[dependsOn[1]]); // HEIGHT

          if (weight > 0 && height > 0) {
            const heightM = height / 100;
            const bmi = weight / (heightM * heightM);
            const precision = field.validation?.precision ?? 1;
            const rounded =
              Math.round(bmi * Math.pow(10, precision)) /
              Math.pow(10, precision);

            const fieldName =
              field.fieldId;
            const currentValue = values[fieldName];
            if (currentValue !== rounded) {
              setValue(fieldName, rounded, { shouldDirty: true });
            }
          } else {
            const fieldName = field.fieldId;
            if (values[fieldName] !== '' && values[fieldName] !== undefined) {
              setValue(fieldName, '', { shouldDirty: false });
            }
          }
        }

        // UNIT_CONVERT: multiply value by conversion factor based on selected unit
        if (formula === 'UNIT_CONVERT' && dependsOn.length === 2) {
          const rawValue = Number(values[dependsOn[0]]);
          const unitValue = values[dependsOn[1]];
          const conversions = field.calculation!.conversions;

          if (rawValue > 0 && unitValue && conversions) {
            const factor = conversions[unitValue];
            let result: number;

            if (factor === 'IFCC_TO_NGSP') {
              // NGSP (%) = (IFCC mmol/mol x 0.0915) + 2.15
              result = (rawValue * 0.0915) + 2.15;
            } else if (typeof factor === 'number') {
              result = rawValue * factor;
            } else {
              return; // Unknown conversion
            }

            const precision = field.validation?.precision ?? 1;
            const rounded =
              Math.round(result * Math.pow(10, precision)) /
              Math.pow(10, precision);

            const fieldName = field.fieldId;
            const currentValue = values[fieldName];
            if (currentValue !== rounded) {
              setValue(fieldName, rounded, { shouldDirty: true });
            }
          } else {
            const fieldName = field.fieldId;
            if (values[fieldName] !== '' && values[fieldName] !== undefined) {
              setValue(fieldName, '', { shouldDirty: false });
            }
          }
        }

        // Add more named formulas here as needed
      });
    });

    return () => subscription.unsubscribe();
  }, [section.fields, watch, setValue]);

  const addRepeat = () => {
    if (repeatCount < maxRepeats) {
      setRepeatCount(prev => prev + 1);
    }
  };

  const removeRepeat = (_index: number) => {
    if (repeatCount > 1) {
      setRepeatCount(prev => prev - 1);
      // TODO: Clear data for removed repeat
    }
  };

  const renderFields = (repeatIndex?: number) => {
    // Track hidden fields so cascading conditions propagate
    const hiddenFields = new Set<string>();

    return section.fields.map((field) => {
      // Check display condition - hide field entirely if not met
      if (field.displayCondition) {
        const depFieldId = field.displayCondition.field;
        // If the field we depend on is itself hidden, hide this field too
        if (hiddenFields.has(depFieldId)) {
          hiddenFields.add(field.fieldId);
          return null;
        }
        const watchValue = watch(depFieldId);
        if (!evaluateCondition(field.displayCondition, watchValue)) {
          hiddenFields.add(field.fieldId);
          return null;
        }
      }

      // Check disable condition - gray out field if condition is met
      let conditionallyDisabled = false;
      if (field.disableCondition) {
        const watchValue = watch(field.disableCondition.field);
        conditionallyDisabled = evaluateCondition(field.disableCondition, watchValue);
      }

      // Check warning condition - show warning when condition is met
      let showWarning = false;
      if (field.warningCondition) {
        const watchValue = watch(field.warningCondition.field);
        showWarning = evaluateCondition(field.warningCondition, watchValue);
      }

      return (
        <FormField
          key={`${field.fieldId}_${repeatIndex ?? 0}`}
          field={field}
          repeatIndex={repeatIndex}
          disabled={disabled}
          conditionallyDisabled={conditionallyDisabled}
          showWarning={showWarning}
        />
      );
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Section Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h2 className="text-lg font-semibold text-gray-800">
          {section.sectionName}
        </h2>
        {section.helpText && (
          <p className="mt-1 text-sm text-gray-500">{section.helpText}</p>
        )}
      </div>

      {/* Section Content */}
      <div className="px-6 py-4">
        {(section.repeatable || section.isRepeating) ? (
          <>
            {Array.from({ length: repeatCount }).map((_, index) => (
              <div key={index} className="relative">
                {repeatCount > 1 && (
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">
                      {section.repeatLabels?.[index] || `${section.repeatLabel || 'Item'} ${index + 1}`}
                    </span>
                    {!disabled && index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeRepeat(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  {renderFields(index)}
                  {/* Source Doc Upload inline for AE_SRCDOC section */}
                  {section.sectionId === 'AE_SRCDOC' && subjectId && (
                    <SourceDocUploadWatcher
                      fieldName={`AESRCTYP_${index}`}
                      section={section}
                      subjectId={subjectId}
                      dataEntryId={dataEntryId}
                      disabled={disabled}
                    />
                  )}
                </div>
                {index < repeatCount - 1 && (
                  <hr className="my-6 border-gray-200" />
                )}
              </div>
            ))}

            {!disabled && repeatCount < maxRepeats && (
              <button
                type="button"
                onClick={addRepeat}
                className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <Plus size={16} />
                Add {section.repeatLabel || 'Item'}
              </button>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {renderFields()}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Watches the AESRCTYP dropdown value and renders SourceDocUpload when a type is selected
 */
const SourceDocUploadWatcher: React.FC<{
  fieldName: string;
  section: FormSectionType;
  subjectId: string;
  dataEntryId?: string;
  disabled: boolean;
}> = ({ fieldName, section, subjectId, dataEntryId, disabled }) => {
  const { watch } = useFormContext();
  const docTypeValue = watch(fieldName);

  if (!docTypeValue) return null;

  // Find the label for this document type from the section's AESRCTYP field options
  const srcTypField = section.fields.find(f => f.fieldId === 'AESRCTYP');
  const option = srcTypField?.options?.find(o => String(o.value) === String(docTypeValue));
  const label = option?.label || String(docTypeValue);

  return (
    <SourceDocUpload
      subjectId={subjectId}
      dataEntryId={dataEntryId}
      documentType={String(docTypeValue)}
      documentTypeLabel={label}
      disabled={disabled}
    />
  );
};

export default FormSection;
